import React, { useState, useEffect, useRef } from 'react';
import PropTypes, { any } from 'prop-types';

import OOPReminderPopup from './components/OOPReminderPopup';

import { titleCase } from '../../utils/formatting';
import { getPlayerName } from '../../utils';
import EventTours from '../../utils/EventTours';
import { isEventType, EventTypes } from '../../utils/events';

import { DisplayStates, OutComeTypes, position1, position2 } from '../../utils/postScoreConstants';

import styles from './matchOutCome.scss';

const MatchOutComeV2 = props => {
    const {
        event,
        displayState,
        postScore,
        user,
        doubles,
        isWinner,
        winningTeam,
        matchOutCome,
        onSelect,
        isPickleball,
        isEvent,
        isExpanded,
        isCompleted,
        hasNoScore,
    } = props;

    const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
    const [showReminder, setShowReminder] = useState(false);
    const [matchOutcomeInfo, setMatchOutcomeInfo] = useState(null);
    const [outcomeSelected, setOutcomeSelected] = useState(null);
    const [teamSelected, setTeamSelected] = useState(null);

    const wrapperRef = useRef(null);
    const isPTTTaged = event?.eventTours.some(({ id }) => id === EventTours.PTT);
    const isFlexLeagueEvent = isEventType(event, EventTypes.FLEX_LEAGUE);

    const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsDropdownExpanded(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        if (matchOutCome !== outcomeSelected) {
            setOutcomeSelected(matchOutCome);
        }
        if (winningTeam !== teamSelected) {
            setTeamSelected(winningTeam);
        }
    }, [matchOutCome, winningTeam]);

    const menuItemClick = (isWinner, completed, matchOutcome, winningTeam) => {
        const hasIMGFeedId = event?.imgFeedId !== null;
        const isOOPSchedule = event?.scheduleType?.id === 2;

        if ((matchOutcome === OutComeTypes.WITHDREW || matchOutcome === OutComeTypes.DEFAULTED) &&
            (isPTTTaged || hasIMGFeedId) && isOOPSchedule) {
            setShowReminder(true);
            setMatchOutcomeInfo({
                isWinner,
                completed,
                matchOutCome: matchOutcome,
                winningTeam,
            });
        } else {
            onSelect(isWinner, completed, matchOutcome, winningTeam);
        }
    };

    const handleOOPReminderPopupClose = () => {
        if (matchOutcomeInfo) {
            const { isWinner, completed, matchOutCome, winningTeam } = matchOutcomeInfo;

            onSelect(isWinner, completed, matchOutCome, winningTeam);
            setShowReminder(false);
        }
    };

    const getPlayerOrTeam = () => {
        if (doubles) {
            return 'Team';
        }
        return 'Player';
    };

    const getPlayerNames = playerType => {
        if (playerType === position1) {
            const pos1player1 = postScore.pos1player1 || user;
            if (doubles) {
                if (postScore.pos1player2) {
                    return `${titleCase(getPlayerName(pos1player1.firstName, pos1player1.lastName))} / 
                    ${titleCase(getPlayerName(postScore.pos1player2.firstName, postScore.pos1player2.lastName))}`;
                }
                return 'Team 1';
            }
            return `${titleCase(getPlayerName(pos1player1.firstName, pos1player1.lastName))}`;
        }
        if (playerType === position2) {
            if (doubles) {
                if (
                    postScore.pos2player1 &&
                    postScore.pos2player2
                ) {
                    const pos2player1 = titleCase(getPlayerName(postScore.pos2player1.firstName, postScore.pos2player1.lastName));
                    const pos2player2 = titleCase(getPlayerName(postScore.pos2player2.firstName, postScore.pos2player2.lastName));
                    return `${pos2player1} / ${pos2player2}`;
                }
                return 'Team 2';
            }
            if (postScore.pos2player1) {
                return titleCase(getPlayerName(postScore.pos2player1.firstName, postScore.pos2player1.lastName));
            }
            return 'Player 2';
        }
        return '';
    };

    return (
        <div className="col-12" ref={wrapperRef}>
            <div
                className={`form-block-divider ${isDropdownExpanded ? 'is-open' : ''} clickable mb16`}
                data-testid="post-score.other-match-outcomes"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsDropdownExpanded(!isDropdownExpanded);
                    isExpanded(!isDropdownExpanded);
                }}
            >
                <div className="title">
                    <div className="text-dark fw600 flex-center">Other Match Outcomes &nbsp;
                        <div className="click-area click-area-32">
                            <i className={`uts uts-${isDropdownExpanded ? 'collapse' : 'expand'}-arrow`} />
                        </div>
                    </div>
                </div>
            </div>
            {isDropdownExpanded && (
                <div className="row">
                    <div className="col-md-6 offset-md-3">
                        <div className="form-group form-group-xl">
                            {(displayState === DisplayStates.WINNER_DECIDED || displayState === DisplayStates.WINNER_NOT_DECIDED) && (
                                <div className={`${styles.outcomeBlock}`}>
                                    <div className={`${styles.outcomeTitle}`}>{`${getPlayerOrTeam()} Retired`}</div>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="retiredTeam2"
                                                id="retiredTeam2"
                                                checked={outcomeSelected === 'retired' && teamSelected === 'team2'}
                                                data-testid="post-score.outcome-retired-team2"
                                                onClick={() => {
                                                    setOutcomeSelected('retired');
                                                    setTeamSelected('team2');
                                                    menuItemClick(true, true, OutComeTypes.RETIRED, 'team2');
                                                }}
                                            />
                                            <label htmlFor="retiredTeam2">{getPlayerNames('pos1')}</label>
                                        </div>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="retiredTeam1"
                                                id="retiredTeam1"
                                                checked={outcomeSelected === 'retired' && teamSelected === 'team1'}
                                                data-testid="post-score.outcome-retired-team1"
                                                onClick={() => {
                                                    setOutcomeSelected('retired');
                                                    setTeamSelected('team1');
                                                    menuItemClick(true, true, OutComeTypes.RETIRED, 'team1');
                                                }}
                                            />
                                            <label htmlFor="retiredTeam1">{getPlayerNames('pos2')}</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!isPickleball && (displayState === DisplayStates.NO_SCORES || isEvent) && (
                                <div className={`${styles.outcomeBlock}`} data-testid="post-score.outcome-defaulted">
                                    <div className={`${styles.outcomeTitle}`}>{`${getPlayerOrTeam()} Defaulted`}</div>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                id="defaultedTeam2"
                                                type="radio"
                                                name="defaultedTeam2"
                                                checked={outcomeSelected === 'defaulted' && teamSelected === 'team2'}
                                                data-testid="post-score.outcome-defaulted-team2"
                                                onClick={() => {
                                                    setOutcomeSelected('defaulted');
                                                    setTeamSelected('team2');
                                                    menuItemClick(true, true, OutComeTypes.DEFAULTED, 'team2');
                                                }}
                                            />
                                            <label htmlFor="defaultedTeam2">{getPlayerNames('pos1')}</label>
                                        </div>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="defaultedTeam1"
                                                id="defaultedTeam1"
                                                checked={outcomeSelected === 'defaulted' && teamSelected === 'team1'}
                                                data-testid="post-score.outcome-defaulted-team1"
                                                onClick={() => {
                                                    setOutcomeSelected('defaulted');
                                                    setTeamSelected('team1');
                                                    menuItemClick(true, true, OutComeTypes.DEFAULTED, 'team1');
                                                }}
                                            />
                                            <label htmlFor="defaultedTeam1">{getPlayerNames('pos2')}</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isPickleball && (
                                <div className={`${styles.outcomeBlock}`}>
                                    <div className={`${styles.outcomeTitle}`}>{`${getPlayerOrTeam()} Forfeited`}</div>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="forfeitedTeam2"
                                                id="forfeitedTeam2"
                                                checked={outcomeSelected === 'forfeit' && teamSelected === 'team2'}
                                                data-testid="post-score.outcome-forfeited-team2"
                                                onClick={() => {
                                                    setOutcomeSelected('forfeit');
                                                    setTeamSelected('team2');
                                                    menuItemClick(true, true, OutComeTypes.FORFEIT, 'team2');
                                                }}
                                            />
                                            <label htmlFor="forfeitedTeam2">{getPlayerNames('pos1')}</label>
                                        </div>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="forfeitedTeam1"
                                                id="forfeitedTeam1"
                                                checked={outcomeSelected === 'forfeit' && teamSelected === 'team1'}
                                                data-testid="post-score.outcome-forfeited-team1"
                                                onClick={() => {
                                                    setOutcomeSelected('forfeit');
                                                    setTeamSelected('team1');
                                                    menuItemClick(true, true, OutComeTypes.FORFEIT, 'team1');
                                                }}
                                            />
                                            <label htmlFor="forfeitedTeam1">{getPlayerNames('pos2')}</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {hasNoScore && (
                                <div className={`${styles.outcomeBlock}`} data-testid="post-score.outcome-withdrew">
                                    <div className={`${styles.outcomeTitle}`}>{`${getPlayerOrTeam()} Withdrew`}</div>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="withdrewTeam2"
                                                checked={outcomeSelected === 'withdrew' && teamSelected === 'team2'}
                                                data-testid="post-score.outcome-withdrew-team2"
                                                onClick={() => {
                                                    setOutcomeSelected('withdrew');
                                                    setTeamSelected('team2');
                                                    menuItemClick(true, true, OutComeTypes.WITHDREW, 'team2');
                                                }}
                                            />
                                            <label htmlFor="withdrewTeam2">{getPlayerNames('pos1')}</label>
                                        </div>
                                        <div className={`${styles.playerName}`}>
                                            <input
                                                type="radio"
                                                name="withdrewTeam1"
                                                id="withdrewTeam1"
                                                checked={outcomeSelected === 'withdrew' && teamSelected === 'team1'}
                                                data-testid="post-score.outcome-withdrew-team1"
                                                onClick={() => {
                                                    setOutcomeSelected('withdrew');
                                                    setTeamSelected('team1');
                                                    menuItemClick(true, true, OutComeTypes.WITHDREW, 'team1');
                                                }}
                                            />
                                            <label htmlFor="withdrewTeam1">{getPlayerNames('pos2')}</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isEvent && !isFlexLeagueEvent && (
                                <div className={`${styles.outcomeBlock}`}>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.outcomeTitle} gap-8`}>
                                            <input
                                                type="radio"
                                                name="abandoned"
                                                id="abandoned"
                                                checked={outcomeSelected === 'abandoned match'}
                                                data-testid="post-score.outcome-abandoned"
                                                onClick={() => {
                                                    setOutcomeSelected('abandoned match');
                                                    menuItemClick(false, displayState === DisplayStates.NO_SCORES, OutComeTypes.ABANDONED, null);
                                                }}
                                            />
                                            <label htmlFor="abandoned" className="fw600">Abandoned Match</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {displayState !== DisplayStates.NO_SCORES && matchOutCome !== OutComeTypes.RETIRED && (
                                <>
                                    {isCompleted && (
                                        <div className={`${styles.outcomeBlock}`}>
                                            <div className={`${styles.playerContainer}`}>
                                                <div className={`${styles.outcomeTitle} gap-8`}>
                                                    <input
                                                        type="radio"
                                                        name="incomplete"
                                                        id="incomplete"
                                                        checked={outcomeSelected === 'incomplete'}
                                                        data-testid="post-score.outcome-incomplete"
                                                        onClick={() => {
                                                            setOutcomeSelected('incomplete');
                                                            menuItemClick(false, false, OutComeTypes.INCOMPLETE, null);
                                                        }}
                                                    />
                                                    <label htmlFor="incomplete" className="fw600">Mark as Incomplete</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!isCompleted && (
                                        <div className={`${styles.outcomeBlock}`}>
                                            <div className={`${styles.playerContainer}`}>
                                                <div className={`${styles.outcomeTitle} gap-8`}>
                                                    <input
                                                        type="radio"
                                                        name="tied match"
                                                        id="tied match"
                                                        checked={outcomeSelected === 'tied match'}
                                                        data-testid="post-score.outcome-tied"
                                                        onClick={() => {
                                                            setOutcomeSelected('tied match');
                                                            menuItemClick(isWinner, true, OutComeTypes.COMPLETE, winningTeam);
                                                        }}
                                                    />
                                                    <label htmlFor="tied match" className="fw600">Mark As Complete</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {hasNoScore && isEvent && !isFlexLeagueEvent && (
                                <div className={`${styles.outcomeBlock}`}>
                                    <div className={`${styles.playerContainer}`}>
                                        <div className={`${styles.outcomeTitle} gap-8`}>
                                            <input
                                                type="radio"
                                                name="doubleWalkover"
                                                id="doubleWalkover"
                                                checked={outcomeSelected === 'double walkover'}
                                                data-testid="post-score.outcome-double-walkover"
                                                onClick={() => {
                                                    setOutcomeSelected('double walkover');
                                                    menuItemClick(false, true, OutComeTypes.DOUBLEWALKOVER, null);
                                                }}
                                            />
                                            <label htmlFor="doubleWalkover" className="fw600">Double Walkover</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showReminder && (
                <OOPReminderPopup
                    onClose={handleOOPReminderPopupClose}
                    cancelAction={() => setShowReminder(false)}
                />
            )}
        </div>
    );
};

MatchOutComeV2.propTypes = {
    event: PropTypes.objectOf(any).isRequired,
    isPickleball: PropTypes.bool,
};

MatchOutComeV2.defaultProps = {
    isPickleball: false,
};

export default MatchOutComeV2;
